(() => {
  const strings = window.ROAMUNO_PLAN_LABEL_STRINGS || {};
  const language = document.documentElement.lang || 'en';
  const number = new Intl.NumberFormat(language, { maximumFractionDigits: 3 });

  const recordForSku = (sku) => {
    const key = String(sku || '').trim();
    return window.ROAMUNO_PLAN_DATA?.bySku?.[key] || window.ROAMUNO_PLAN_LABEL_DATA?.bySku?.[key] || null;
  };

  const formatData = (record) => {
    if (record?.planFamily === 'daily_unlimited') {
      const amount = Number(record?.dailyHighSpeedGb ?? record?.dataGb);
      const highSpeed = Number.isFinite(amount) && amount > 0
        ? (strings.highSpeedDaily || '%%COUNT%% GB high-speed data daily').replaceAll('%%COUNT%%', number.format(amount))
        : null;
      return [strings.unlimited || 'Unlimited', highSpeed].filter(Boolean).join(' · ');
    }
    const amount = Number(record?.dataGb);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    if (amount < 1) return `${number.format(Math.round(amount * 1000))} MB`;
    return `${number.format(amount)} GB`;
  };

  const formatValidity = (record) => {
    const days = Number(record?.validityDays);
    if (!Number.isInteger(days) || days <= 0) return null;
    const template = days === 1 ? strings.dayOne : strings.dayOther;
    return typeof template === 'string' && template
      ? template.replaceAll('%%COUNT%%', number.format(days))
      : null;
  };

  const formatDailyQuota = (record) => {
    const amount = Number(record?.dailyHighSpeedGb ?? record?.dataGb);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return (strings.dailyQuotaCompact || '%%COUNT%% GB/day').replaceAll('%%COUNT%%', number.format(amount));
  };

  const formatPlan = (record) => {
    const data = formatData(record);
    const validity = formatValidity(record);
    if (!data || !validity) return null;
    if (record?.planFamily !== 'daily_unlimited') return `${data} / ${validity}`;
    const dailyQuota = formatDailyQuota(record);
    return dailyQuota ? `${strings.unlimited || 'Unlimited'} · ${dailyQuota} · ${validity}` : null;
  };

  const formatPlanOption = (record) => {
    if (record?.planFamily !== 'daily_unlimited') return formatPlan(record);
    return formatValidity(record);
  };

  const formatPlanOptionSummary = (record) => {
    if (!record) return { primary: null, secondary: null };
    let primary = null;
    if (record.planFamily === 'daily_unlimited') {
      const amount = Number(record.dailyHighSpeedGb ?? record.dataGb);
      const reducedSpeed = typeof record.reducedSpeed === 'string' ? record.reducedSpeed.trim() : '';
      if (Number.isFinite(amount) && amount > 0 && reducedSpeed) {
        primary = (strings.fupSummary || 'Lower speed rate of %%SPEED%% after %%COUNT%% GB usage per day.')
          .replaceAll('%%SPEED%%', reducedSpeed)
          .replaceAll('%%COUNT%%', number.format(amount));
      }
    }
    return { primary, secondary: null };
  };

  const localizeProductPicker = (root = document) => {
    const variantsNode = root.querySelector?.('[data-product-variants]') || document.querySelector('[data-product-variants]');
    let variants = [];
    try {
      variants = JSON.parse(variantsNode?.textContent || '[]');
    } catch (_) {
      return;
    }
    const byTitle = new Map(variants.map((variant) => [String(variant.title || ''), variant]));
    root.querySelectorAll?.('variant-selects [data-plan-option-label]').forEach((node) => {
      const input = node.matches('option') ? node : node.parentElement?.previousElementSibling;
      const variant = byTitle.get(String(node.dataset.optionValue || input?.value || ''));
      const label = formatPlanOption(recordForSku(variant?.sku));
      if (!label) return;
      if (node.matches('option')) {
        const template = node.dataset.optionAvailable === 'false' ? strings.unavailable : null;
        const text = template ? template.replaceAll('%%VALUE%%', label) : label;
        if (node.textContent !== text) node.textContent = text;
      } else {
        if (node.textContent !== label) node.textContent = label;
        const cardMain = node.parentElement?.querySelector?.('[data-plan-card-main]');
        const cardDays = node.parentElement?.querySelector?.('[data-plan-card-days]');
        const [main, ...days] = label.split(' / ');
        if (cardMain && cardMain.textContent !== main) cardMain.textContent = main;
        const duration = days.join(' / ');
        if (cardDays && cardDays.textContent !== duration) cardDays.textContent = duration;
      }
    });
    root.querySelectorAll?.('variant-selects [data-plan-option-summary]').forEach((node) => {
      const variant = byTitle.get(String(node.dataset.optionValue || ''));
      const summary = formatPlanOptionSummary(recordForSku(variant?.sku));
      const primary = node.querySelector?.('[data-plan-option-summary-primary]');
      if (primary) {
        const primaryText = summary.primary || '';
        if (primary.textContent !== primaryText) primary.textContent = primaryText;
        primary.hidden = !summary.primary;
      }
      node.hidden = !summary.primary;
    });
  };

  const localizeCart = (root = document) => {
    root.querySelectorAll?.('[data-plan-line-label][data-plan-sku]').forEach((node) => {
      const label = formatPlan(recordForSku(node.dataset.planSku));
      if (label && node.textContent !== label) node.textContent = label;
    });
  };

  const localize = (root = document) => {
    localizeProductPicker(root);
    localizeCart(root);
  };

  window.ROAMUNO_PLAN_LABELS = { formatData, formatValidity, formatDailyQuota, formatPlan, formatPlanOption, formatPlanOptionSummary, recordForSku, localize };

  const start = () => {
    localize();
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        localize();
      });
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
